<template>
  <div class="flex flex-col gap-1">
    <div
      v-for="item in entries"
      :key="item.value"
      class="text-left flex-align pl-4 py-1 pr-1 gap-2 border rounded-md"
    >
      <span class="text-left text-truncate">
        {{ item.title }}
      </span>
      <v-spacer />
      <v-number-input
        v-model="item.quantity"
        :min="limit.min"
        :max="limit.max"
        :readonly="readonly"
        :disabled="disabled"
        max-width="150px"
        variant="outlined"
        control-variant="split"
        density="compact"
        hide-details
        inset
      />
    </div>
  </div>
</template>

<script lang="ts">
import { get, isObject } from "lodash";
import { defineComponent } from "vue";

export type QuantityListInputSelection<
  T = any,
  K extends PropertyKey = string
> = {
  item: T;
  value: K;
  quantity: number;
};

export default defineComponent({
  name: "QuantityListInput",
  props: {
    modelValue: {
      type: Array as PropType<QuantityListInputSelection[]>,
      required: true,
    },
    items: {
      type: Array as PropType<any[]>,
      required: true,
    },
    itemTitle: { type: String, default: "title" },
    itemValue: { type: String, default: "value" },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    min: { type: [Number, String], default: 0 },
    max: { type: [Number, String], default: 100 },
  },
  emits: ["update:modelValue"],
  setup(props, ctx) {
    const selection = computed<QuantityListInputSelection[]>({
      get() {
        return props.modelValue || [];
      },
      set(value: QuantityListInputSelection[]) {
        ctx.emit("update:modelValue", value);
      },
    });

    const getValue = (item: any) => {
      return isObject(item) ? get(item, props.itemValue, item) : item;
    };
    const getTitle = (item: any) => {
      return isObject(item) ? get(item, props.itemTitle, item) : item;
    };

    const getQuantity = (item: any) => {
      return (
        selection.value.find((selection) => selection.value === getValue(item))
          ?.quantity || 0
      );
    };

    const setQuantity = (item: any, quantity: number) => {
      const selections: QuantityListInputSelection[] = [...selection.value];
      const value = getValue(item);
      const existingIndex = selections.findIndex(
        (selection) => selection.value === value
      );

      if (quantity > 0) {
        if (existingIndex !== -1) {
          // Update existing selection
          selections[existingIndex].quantity = quantity;
        } else {
          // Add new selection
          selections.push({
            value,
            quantity,
            item: isObject(item) ? markRaw(item) : item,
          });
        }
      } else if (existingIndex !== -1) {
        // Remove selection when quantity is 0 or less
        selections.splice(existingIndex, 1);
      }

      selection.value = selections;
    };

    const limit = computed(() => {
      return {
        min: Number(props.min),
        max: Number(props.max),
      };
    });

    const entries = computed(() => {
      return props.items.map((item: any) => ({
        title: getTitle(item),
        value: getValue(item),
        get quantity() {
          return getQuantity(item);
        },
        set quantity(value: number) {
          setQuantity(item, value);
        },
      }));
    });

    return {
      entries,
      limit,
      selection,
    };
  },
});
</script>
